import { Fragment, useEffect, useMemo, useState } from 'react';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { useFormik } from 'formik';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import {
  Checkbox,
  FormControlLabel,
  Tooltip as TooltipMaterial,
  tooltipClasses,
  TooltipProps,
  Typography,
  useEventCallback,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArchiveAnnotationsPageCrumbs } from 'civil-status/constants/breadcrumb';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import {
  ArchiveAnnotationsForm,
  ArchiveAnnotationsListVM,
  ArchivioAnnotazioni,
  initialValues,
} from 'civil-status/view-models/archive-annotations-list';
import FiltersArchiveAnnotations from 'civil-status/components/archive-annotations/filter';
import { transformDatasetArchiveAnnotations } from 'civil-status/utils/transformer-archive-annotations/transform-dataset';
import { transformListArchiveAnnotations } from 'civil-status/utils/transformer-archive-annotations/transformer-list';
import { HttpResponse } from 'shell/models/http-response';
import TableArchiveAnnotations from 'civil-status/components/archive-annotations/table';
import { validationSchemaArchiveAnnotations } from 'civil-status/constants/schema-archive-annotations';
import ArchiveAnnotationsModal, {
  ModalFormVM,
} from 'civil-status/components/archive-annotations/modal';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import {
  CODE_STATE_REJECTED,
  nameCodeStateArchiveAnnotations,
} from 'civil-status/constants/code-state-archive-annotations';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccess from 'practice/components/detail/form/print-comunication/modal';
import { getNameRegister } from 'civil-status/components/shared/list-register';

const Tooltip = styled(({ className, ...props }: TooltipProps) => (
  <TooltipMaterial {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '1rem',
  },
}));

const ArchiveAnnotationsPage = (): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState({
    open: false,
    message: '',
    isRegistration: false,
    isReject: false,
  });
  const [pdfModal, setPdfModal] = useState({ data: '', open: false, name: '' });
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{
    open: boolean;
    total?: number;
  }>({
    open: false,
  });

  const filterTable = useFilterTable<ArchiveAnnotationsForm>();

  const [annotations, getAnnotations] = useHttpFetch<
    HttpResponse<Array<ArchivioAnnotazioni>>,
    Array<ArchiveAnnotationsListVM>
  >(`${__ACT_API_URL__}/archivio`, {
    onBootstrap: false,
    transformer: transformListArchiveAnnotations,
  });

  const dataList = useMemo(() => {
    return (annotations.data as Array<ArchiveAnnotationsListVM>) || [];
  }, [annotations.data]);

  useEffect(() => {
    if (dataList.length) {
      setOpen(false);
    }
  }, [dataList]);

  const onSubmit = useEventCallback((formValues: ArchiveAnnotationsForm) => {
    const queryString = transformDatasetArchiveAnnotations({ ...formValues, page: 0 });
    formContext.setFieldValue('page', 0);
    getAnnotations({ params: {}, paramsSerializer: () => queryString });
  });

  const formContext = useFormik<ArchiveAnnotationsForm>({
    initialValues: initialValues,
    validationSchema: validationSchemaArchiveAnnotations,
    onSubmit,
  });

  // useEffect(() => {
  //   formContext.setValues(filterTable.values || initialValues);
  //   const queryString = transformDatasetArchiveAnnotations(filterTable.values || initialValues);
  //   getAnnotations({ params: {}, paramsSerializer: () => queryString });
  // }, []);

  useEffect(() => {
    return () => filterTable.setValues(formContext.values);
  }, [formContext.values]);

  const onChangeCheckbox = useEventCallback((element: ArchiveAnnotationsListVM) => {
    let newArr = (formContext.values.itemsSelected || []).slice();

    if (newArr.includes(element.id)) {
      // elemento da eliminare
      newArr = newArr.filter((item) => item !== element.id);

      formContext.setValues({
        ...(formContext.values || {}),
        isSelectedAll: false,
        itemsSelected: newArr,
      });
    } else {
      // elemento da inserire
      newArr.push(element.id);

      formContext.setValues({
        ...(formContext.values || {}),
        isSelectedAll: newArr.length === dataList.length,
        itemsSelected: newArr,
      });
    }
  });

  const body: Array<ArchiveAnnotationsListVM> = useMemo(() => {
    return (dataList || []).map((item, index) => {
      const stateLabel = nameCodeStateArchiveAnnotations(item.codeState || '');
      return {
        ...item,
        state:
          item.codeState === CODE_STATE_REJECTED && item.rejectReason ? (
            <Tooltip title={item.rejectReason}>
              <span>{stateLabel}</span>
            </Tooltip>
          ) : (
            stateLabel
          ),
        practice: getNameRegister(item.practice || ''),
        action: (
          <FormControlLabel
            control={
              <Checkbox
                id={`isSelected-${index}`}
                checked={formContext.values?.itemsSelected?.includes(item.id)}
                onChange={() => onChangeCheckbox(item)}
                inputProps={{ 'aria-label': 'Seleziona riga' }}
              />
            }
            label=""
          />
        ),
      };
    });
  }, [dataList, formContext.values?.itemsSelected]);

  const onClickRegistration = useEventCallback(() => {
    const arrLength = formContext.values.itemsSelected?.length;
    if (arrLength) {
      setOpenModal({
        open: true,
        message: `Confermi di voler registrare ${arrLength} annotazioni?`,
        isRegistration: true,
        isReject: false,
      });
    }
  });

  const onClickPrint = useEventCallback(() => {
    const arrLength = formContext.values.itemsSelected?.length;
    if (arrLength) {
      setOpenModal({
        open: true,
        message: `Confermi di voler stampare ${arrLength} annotazioni?`,
        isRegistration: false,
        isReject: false,
      });
    }
  });

  const onClickReject = useEventCallback(() => {
    const arrLength = formContext.values.itemsSelected?.length;
    if (arrLength) {
      setOpenModal({
        open: true,
        message: `Confermi di voler rifiutare ${arrLength} annotazioni?`,
        isRegistration: false,
        isReject: true,
      });
    }
  });

  const onRegistration = useHttpClient(`${__ACT_API_URL__}/archivio/registrazione`, {
    options: {
      method: 'PUT',
      loading: true,
      errorCatching: true,
    },
  });

  const onConfirmModalRegistration = useEventCallback((values: ModalFormVM) => {
    (
      onRegistration({
        data: {
          idComunicazioneList: formContext.values.itemsSelected || [],
          dataRegistrazione: values.dateRegistration
            ? values.dateRegistration.toFormat('yyyy-MM-dd')
            : undefined,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      onSubmit(formContext.values);
      setOpenModal({ open: false, message: '', isRegistration: false, isReject: false });
      formContext.setFieldValue('itemsSelected', []);
    });
  });

  const onReject = useHttpClient(`${__ACT_API_URL__}/archivio/rifiuta`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onConfirmModalReject = useEventCallback((values: ModalFormVM) => {
    (
      onReject({
        data: {
          idComunicazioneList: formContext.values.itemsSelected || [],
          testoMotivoRifiuto: values.rejectReason,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      onSubmit(formContext.values);
      setOpenModal({ open: false, message: '', isRegistration: false, isReject: false });
      formContext.setFieldValue('itemsSelected', []);
    });
  });

  const onPrint = useHttpClient<Blob>(`${__ACT_API_URL__}/archivio/stampa`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onConfirmModalPrint = useEventCallback(() => {
    const dataListCopy = dataList.slice();
    const datiComunicazioneList: Array<ArchivioAnnotazioni> = dataListCopy
      .filter((item) => (formContext.values.itemsSelected || []).includes(item.id))
      .map((itemFiltred) => ({ ...itemFiltred.itemArchivioAnnotazioni }));

    (
      onPrint({
        data: {
          datiComunicazioneList,
        },
        responseType: 'blob',
      }) as ResponsePromise<Blob>
    ).then((response) => {
      if (response?.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name: 'Annotazioni archivio',
            open: true,
          });
        });
      }
      onSubmit(formContext.values);
      setOpenModal({ open: false, message: '', isRegistration: false, isReject: false });
      formContext.setFieldValue('itemsSelected', []);
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={ArchiveAnnotationsPageCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Annotazioni archivio
        </Typography>
      </div>
      <div className="pmi-my-3">
        <FiltersArchiveAnnotations filters={formContext} setOpen={setOpen} open={open} />
      </div>
      {annotations.data ? (
        dataList?.length > 0 ? (
          <TableArchiveAnnotations
            body={body}
            formContext={formContext}
            lengthList={dataList.length}
            onClickRegistration={onClickRegistration}
            onClickPrint={onClickPrint}
            onClickReject={onClickReject}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <ArchiveAnnotationsModal
        open={openModal.open}
        message={openModal.message}
        onClose={() =>
          setOpenModal({ open: false, message: '', isRegistration: false, isReject: false })
        }
        onConfirm={
          openModal.isReject
            ? onConfirmModalReject
            : openModal.isRegistration
            ? onConfirmModalRegistration
            : onConfirmModalPrint
        }
        isRegistration={openModal.isRegistration}
        isReject={openModal.isReject}
      />
      {pdfModal.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data}
          name={pdfModal.name}
          title={pdfModal.name || 'Anteprima atto'}
          onClose={() => {
            setModalPrintSuccess((oldState) => ({
              ...oldState,
              open: true,
            }));
            setPdfModal({ data: '', open: false, name: '' });
          }}
        />
      ) : undefined}
      {modalPrintSuccess.open ? (
        <ModalPrintSuccess
          open={modalPrintSuccess.open}
          onYes={() => setModalPrintSuccess({ open: false })}
          onClose={onConfirmModalPrint}
        />
      ) : undefined}
    </Fragment>
  );
};

ArchiveAnnotationsPage.displayName = 'Archive Annotations Page component';

export default ArchiveAnnotationsPage;
